import { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { MatchEvent } from "../../../../store/useMatchLogStore";
import TeamLogo, { type TeamLogoProps } from "../../../../components/TeamLogo";
import { useMatchMomentum } from "./useMomentumAnalytics";
import {
  DEFAULT_HOME_COLOR,
  DEFAULT_AWAY_COLOR,
  resolveChartColor,
} from "../chartColor";
import { resolveMomentumColors, momentumStrokeFor } from "./momentumColors";

// Team crests grow from "md" (40px) to "lg" (48px) at the `sm` breakpoint —
// a single DOM node via a `!`-important className override, since
// `TeamLogo`'s size prop sets an inline `style` that a plain Tailwind class
// cannot outrank. Shared by both crests here.
const CREST_RESPONSIVE_CLASS = "sm:!w-12 sm:!h-12 sm:!min-w-12";
// Left gutter reserved for the crest column (PMA-COCK-161) so the chart's
// areas/axes never paint underneath either crest size.
const CREST_GUTTER_PX = 56;

// Standard match-minute ticks; filtered down to whatever the data actually covers.
const GOAL_ICON_OFFSET = 14;
// Empty category slots between two period groups (the visible gap).
const GROUP_GAP_SLOTS = 3;

// Nominal minute range of each period group (clock minutes, extra time in
// 15' halves). Used to lay out the 15' tick labels per group.
const PERIOD_RANGE: Record<number, [number, number]> = {
  1: [0, 45],
  2: [45, 90],
  3: [90, 105],
  4: [105, 120],
};

function useNarrowViewport(): boolean {
  const query = () =>
    typeof window !== "undefined" && window.innerWidth > 0
      ? window.innerWidth < 480
      : false;
  const [narrow, setNarrow] = useState(query);
  useEffect(() => {
    const onResize = () => setNarrow(query());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return narrow;
}

interface GoalBallLabelProps {
  viewBox?: { x?: number; y?: number; height?: number };
  haloColor: string;
  minute: number;
  side: "home" | "away";
  teamLabel: string;
}

// EPIC-110 D4: the goal ball sits OUTSIDE the plot area, above the top (home)
// band for a home goal and below the bottom (away) band for an away goal.
function goalAnchorCy(
  viewBox: { y?: number; height?: number } | undefined,
  side: "home" | "away",
): number | undefined {
  if (viewBox?.y === undefined) return undefined;
  return side === "home"
    ? viewBox.y - GOAL_ICON_OFFSET
    : viewBox.y + (viewBox.height ?? 0) + GOAL_ICON_OFFSET;
}

// PMA-COCK-192: dismissal markers stay inside the plot, at the top for a
// home card and the bottom for an away card, on the offending team's side.
function anchorCy(
  viewBox: { y?: number; height?: number } | undefined,
  side: "home" | "away",
): number | undefined {
  if (viewBox?.y === undefined) return undefined;
  return side === "home"
    ? viewBox.y + 10
    : viewBox.y + (viewBox.height ?? 0) - 10;
}

// Broadcast-style goal marker: a recognizable soccer ball (white, with the
// classic black pentagon/seam pattern) so it reads as "a ball" first; a
// subtle team-colored halo behind it indicates who scored.
function GoalBallLabel({
  viewBox,
  haloColor,
  minute,
  side,
  teamLabel,
}: GoalBallLabelProps) {
  if (viewBox?.x === undefined || viewBox?.y === undefined) return null;
  const cx = viewBox.x;
  const cy = goalAnchorCy(viewBox, side)!;
  const pentagon = [
    [cx, cy - 2.7],
    [cx + 2.6, cy - 0.85],
    [cx + 1.6, cy + 2.2],
    [cx - 1.6, cy + 2.2],
    [cx - 2.6, cy - 0.85],
  ] as const;
  const seams = [
    [cx, cy - 2.7, cx, cy - 6.6],
    [cx + 2.6, cy - 0.85, cx + 5.7, cy - 2.6],
    [cx + 1.6, cy + 2.2, cx + 3.6, cy + 5.4],
    [cx - 1.6, cy + 2.2, cx - 3.6, cy + 5.4],
    [cx - 2.6, cy - 0.85, cx - 5.7, cy - 2.6],
  ] as const;
  return (
    <g data-testid={`momentum-goal-ball-${side}-${minute}`}>
      <title>{`${teamLabel} ${minute}'`}</title>
      <circle
        cx={cx}
        cy={cy}
        r={9}
        fill={haloColor}
        opacity={0.4}
        stroke={momentumStrokeFor(haloColor)}
        strokeWidth={1}
      />
      <circle
        cx={cx}
        cy={cy}
        r={7}
        fill="#ffffff"
        stroke="#111827"
        strokeWidth={1.1}
      />
      <polygon
        points={pentagon.map(([x, y]) => `${x},${y}`).join(" ")}
        fill="#111827"
      />
      {seams.map(([x1, y1, x2, y2], i) => (
        <line
          key={i}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke="#111827"
          strokeWidth={1}
        />
      ))}
    </g>
  );
}

interface RedCardLabelProps {
  viewBox?: { x?: number; y?: number; height?: number };
  minute: number;
  side: "home" | "away";
  teamLabel: string;
  kind: "red" | "secondYellow";
  t: (key: string, fallback?: string) => string;
}

// PMA-COCK-192: any dismissal (a straight Red, or a Yellow (Second) — see
// computeMomentum's cardMarkerKind) draws a small rounded red-card marker on
// the offending team's side, same vertical convention as GoalBallLabel. A
// fixed red fill, not team-colored — it must always read as "a red card"
// regardless of either team's own palette — with a thin darker border so it
// survives the dark card background.
function RedCardLabel({
  viewBox,
  minute,
  side,
  teamLabel,
  kind,
  t,
}: RedCardLabelProps) {
  if (viewBox?.x === undefined || viewBox?.y === undefined) return null;
  const cx = viewBox.x;
  const cy = anchorCy(viewBox, side)!;
  const width = 8;
  const height = 11;
  return (
    <g data-testid={`momentum-card-${kind}-${side}-${minute}`}>
      <title>{`${t("analytics.momentumDismissal", "Sent off")} — ${teamLabel} ${minute}'`}</title>
      <rect
        x={cx - width / 2}
        y={cy - height / 2}
        width={width}
        height={height}
        rx={1.5}
        fill="#dc2626"
        stroke="#7f1d1d"
        strokeWidth={1}
      />
    </g>
  );
}

export interface MatchMomentumChartProps {
  events: MatchEvent[];
  homeTeamId: string;
  awayTeamId: string;
  homeLabel: string;
  awayLabel: string;
  homeTeamColor?: string;
  awayTeamColor?: string;
  /**
   * Secondary team colors (PMA-COCK-180) — used by `resolveChartTeamPair`
   * as the chromatic fallback when `homeTeamColor`/`awayTeamColor` is
   * achromatic (black/near-black) and can't be brightened into something
   * that still reads as the team's own color. Optional; omitting either
   * behaves exactly as before this UC.
   */
  homeTeamSecondaryColor?: string;
  awayTeamSecondaryColor?: string;
  /**
   * Team objects (PMA-COCK-161) used to render crests at the left edge of
   * the chart — home in the upper half, away in the lower half. Optional;
   * omitting either/both reproduces today's exact layout (no gutter
   * reserved, no crest rendered).
   */
  homeTeam?: TeamLogoProps["team"];
  awayTeam?: TeamLogoProps["team"];
  t: (key: string, fallback?: string) => string;
}

interface ChartSlot {
  idx: number;
  gap: boolean;
  period: number;
  minute: number;
  home: number;
  away: number;
  value: number; // home - away
}

interface BarShapeProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  payload?: ChartSlot;
  homeColor: string;
  awayColor: string;
}

// Diverging bar: positive (home) goes up in the home colour, negative (away)
// goes down in the away colour. Recharts may hand a negative height for a
// negative value, so normalise to a positive-sized rect.
function MomentumBarShape({
  x,
  y,
  width,
  height,
  payload,
  homeColor,
  awayColor,
}: BarShapeProps) {
  if (!payload || payload.gap || !payload.value) return null;
  if (x === undefined || y === undefined) return null;
  const h = Math.abs(height ?? 0);
  const top = Math.min(y, y + (height ?? 0));
  const side = payload.value > 0 ? "home" : "away";
  return (
    <rect
      data-testid={`momentum-bar-${side}-${payload.period}-${payload.minute}`}
      x={x}
      y={top}
      width={Math.max(width ?? 0, 1)}
      height={Math.max(h, 1)}
      rx={1}
      fill={side === "home" ? homeColor : awayColor}
      stroke={momentumStrokeFor(side === "home" ? homeColor : awayColor)}
      strokeWidth={1}
    />
  );
}

export function MatchMomentumChart({
  events,
  homeTeamId,
  awayTeamId,
  homeLabel,
  awayLabel,
  homeTeamColor,
  awayTeamColor,
  homeTeamSecondaryColor,
  awayTeamSecondaryColor,
  homeTeam,
  awayTeam,
  t,
}: MatchMomentumChartProps) {
  const [explainOpen, setExplainOpen] = useState(false);
  const HOME_COLOR = homeTeamColor || DEFAULT_HOME_COLOR;
  const AWAY_COLOR = awayTeamColor || DEFAULT_AWAY_COLOR;

  // EPIC-110 D4: momentum-only resolver. Near-neutral kits (black, white,
  // grey) fall back to green / blue and the pair is always distinguishable.
  // The legend swatches, captions and bars all use these same two colours.
  const { home: HOME_FILL, away: AWAY_FILL } = useMemo(
    () =>
      resolveMomentumColors(
        { primary: HOME_COLOR, secondary: homeTeamSecondaryColor },
        { primary: AWAY_COLOR, secondary: awayTeamSecondaryColor },
      ),
    [HOME_COLOR, AWAY_COLOR, homeTeamSecondaryColor, awayTeamSecondaryColor],
  );

  // Only TEXT (captions, tooltip lines) keeps the PMA-COCK-180 contrast floor
  // (a black kit reads as light neutral there). Every colour fill (bars,
  // swatches, markers, accent bar, header strip) draws the true kit colour
  // with an outline when too dark for the card (EPIC-111 D2).
  const NEEDS_RING =
    !!momentumStrokeFor(HOME_FILL) || !!momentumStrokeFor(AWAY_FILL);
  const RING = NEEDS_RING ? "inset 0 0 0 1px #cbd5e1" : undefined;
  const HOME_TEXT = resolveChartColor(
    HOME_FILL,
    homeTeamSecondaryColor,
    "fill",
  );
  const AWAY_TEXT = resolveChartColor(
    AWAY_FILL,
    awayTeamSecondaryColor,
    "fill",
  );

  const { points, bars, goals, cards, goalPeriods, cardPeriods, usedFallback } =
    useMatchMomentum(events, {
      homeTeamId,
      awayTeamId,
    });

  const hasData = points.some((p) => p.home !== 0 || p.away !== 0);

  // One slot per (period, minute) bar, with GROUP_GAP_SLOTS empty slots
  // between period groups. Slot index is the categorical x value.
  const narrow = useNarrowViewport();
  const layout = useMemo(() => {
    const slots: ChartSlot[] = [];
    const slotByKey = new Map<string, number>();
    const groupGaps: { afterPeriod: number; midIdx: number }[] = [];
    const ticks: number[] = [];
    const periods = [...new Set(bars.map((b) => b.period))].sort(
      (x, y) => x - y,
    );
    let prevPeriod: number | null = null;
    for (const period of periods) {
      if (prevPeriod !== null) {
        const first = slots.length;
        for (let g = 0; g < GROUP_GAP_SLOTS; g++) {
          slots.push({
            idx: slots.length,
            gap: true,
            period: prevPeriod,
            minute: -1,
            home: 0,
            away: 0,
            value: 0,
          });
        }
        groupGaps.push({
          afterPeriod: prevPeriod,
          midIdx: first + Math.floor(GROUP_GAP_SLOTS / 2),
        });
      }
      prevPeriod = period;
      const own = bars.filter((b) => b.period === period);
      const byMinute = new Map(own.map((b) => [b.minute, b]));
      const firstMin = own[0].minute;
      const lastMin = own[own.length - 1].minute;
      // Each group spans its full nominal range (0-45, 45-90, 90-105,
      // 105-120) even where no event was logged, so the 15' ticks exist.
      const nominal = PERIOD_RANGE[period];
      const start = nominal ? Math.min(nominal[0], firstMin) : firstMin;
      const end = nominal ? Math.max(nominal[1], lastMin) : lastMin;
      let tickOrdinal = 0;
      for (let minute = start; minute <= end; minute++) {
        const idx = slots.length;
        slotByKey.set(`${period}:${minute}`, idx);
        const bar = byMinute.get(minute);
        if (minute % 15 === 0) {
          // Narrow screens keep every other tick (labels never collide).
          if (!narrow || tickOrdinal % 2 === 0) ticks.push(idx);
          tickOrdinal += 1;
        }
        slots.push({
          idx,
          gap: false,
          period,
          minute,
          home: bar?.home ?? 0,
          away: bar?.away ?? 0,
          value: bar?.momentum ?? 0,
        });
      }
    }
    const maxAbs = slots.reduce((m, s) => Math.max(m, Math.abs(s.value)), 0);
    return {
      slots,
      slotByKey,
      groupGaps,
      ticks,
      bound: maxAbs > 0 ? maxAbs * 1.1 : 1,
    };
  }, [bars, narrow]);

  const minuteBySlot = useMemo(
    () => new Map(layout.slots.map((s) => [s.idx, s.minute])),
    [layout.slots],
  );

  return (
    <div
      data-testid="momentum-card"
      className="relative overflow-hidden rounded-2xl bg-slate-900 shadow-2xl ring-1 ring-white/10"
    >
      {/* Team-colored header strip */}
      <div
        aria-hidden="true"
        data-testid="momentum-header-strip"
        className="flex h-1.5 w-full"
        style={{ boxShadow: RING }}
      >
        <div className="h-full flex-1" style={{ background: HOME_FILL }} />
        <div className="h-full flex-1" style={{ background: AWAY_FILL }} />
      </div>

      <div className="bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 p-4 text-white sm:p-5 md:p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold font-condensed uppercase tracking-wide">
            {t("analytics.momentumTitle", "Match Momentum")}
          </h3>
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1">
              <span
                data-testid="momentum-legend-home"
                className="inline-block w-3 h-3 rounded-sm"
                style={{
                  background: HOME_FILL,
                  ...(momentumStrokeFor(HOME_FILL)
                    ? { border: `1px solid ${momentumStrokeFor(HOME_FILL)}` }
                    : {}),
                }}
              />
              {homeLabel}
            </span>
            <span className="flex items-center gap-1">
              <span
                data-testid="momentum-legend-away"
                className="inline-block w-3 h-3 rounded-sm"
                style={{
                  background: AWAY_FILL,
                  ...(momentumStrokeFor(AWAY_FILL)
                    ? { border: `1px solid ${momentumStrokeFor(AWAY_FILL)}` }
                    : {}),
                }}
              />
              {awayLabel}
            </span>
          </div>
        </div>

        {!hasData ? (
          <p
            className="text-sm text-slate-400 py-8 text-center"
            data-testid="momentum-empty"
          >
            {t(
              "analytics.momentumEmpty",
              "Not enough data yet to show momentum",
            )}
          </p>
        ) : (
          <>
            {/* Top directional caption: home above zero axis */}
            <div
              className="flex items-center gap-1 text-[11px] mb-1 select-none"
              style={{ color: HOME_TEXT }}
            >
              <span aria-hidden="true">▲</span>
              <span>
                {t(
                  "analytics.momentumHomeThreat",
                  "{{team}} more threatening",
                ).replace("{{team}}", homeLabel)}
              </span>
            </div>

            <div className="relative">
              {(homeTeam || awayTeam) && (
                <div
                  aria-hidden="true"
                  data-testid="momentum-crests"
                  className="pointer-events-none absolute inset-y-0 left-0 z-10 flex w-14 flex-col"
                >
                  <div className="flex flex-1 items-center justify-center">
                    {homeTeam && (
                      <TeamLogo
                        team={homeTeam}
                        bare
                        size="md"
                        className={CREST_RESPONSIVE_CLASS}
                        testId="momentum-home-crest"
                      />
                    )}
                  </div>
                  <div className="flex flex-1 items-center justify-center">
                    {awayTeam && (
                      <TeamLogo
                        team={awayTeam}
                        bare
                        size="md"
                        className={CREST_RESPONSIVE_CLASS}
                        testId="momentum-away-crest"
                      />
                    )}
                  </div>
                </div>
              )}
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={layout.slots}
                  barCategoryGap={1}
                  margin={{
                    top: 30,
                    right: 8,
                    left: homeTeam || awayTeam ? CREST_GUTTER_PX : 8,
                    bottom: 30,
                  }}
                >
                  <XAxis
                    dataKey="idx"
                    ticks={layout.ticks}
                    interval={0}
                    tickMargin={24}
                    tick={(props: {
                      x?: number;
                      y?: number;
                      payload?: { value: number };
                    }) => (
                      <text
                        data-testid="momentum-x-tick"
                        x={props.x}
                        y={props.y}
                        dy={4}
                        textAnchor="middle"
                        fill="#e2e8f0"
                        fontSize={11}
                      >
                        {`${minuteBySlot.get(props.payload?.value ?? -1)}'`}
                      </text>
                    )}
                  />
                  <YAxis hide domain={[-layout.bound, layout.bound]} />
                  <CartesianGrid
                    stroke="#94a3b8"
                    strokeOpacity={0.2}
                    strokeDasharray="3 3"
                    vertical={false}
                  />

                  {/* Faint tinted bands: home above the zero line, away below */}
                  <ReferenceArea
                    y1={0}
                    y2={layout.bound}
                    fill={HOME_FILL}
                    fillOpacity={0.08}
                    stroke="none"
                    data-testid="momentum-band-home"
                  />
                  <ReferenceArea
                    y1={-layout.bound}
                    y2={0}
                    fill={AWAY_FILL}
                    fillOpacity={0.08}
                    stroke="none"
                    data-testid="momentum-band-away"
                  />

                  <Tooltip
                    cursor={{ fill: "#94a3b8", fillOpacity: 0.12 }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const pt = payload[0]?.payload as ChartSlot | undefined;
                      if (!pt || pt.gap) return null;
                      return (
                        <div
                          style={{
                            background: "#1e293b",
                            border: "1px solid #64748b",
                            borderRadius: 8,
                            color: "#fff",
                            padding: "6px 10px",
                            fontSize: 12,
                            lineHeight: 1.6,
                          }}
                        >
                          <div
                            style={{
                              color: "#cbd5e1",
                              fontWeight: 600,
                              marginBottom: 2,
                            }}
                          >
                            {`${pt.minute}'`}
                          </div>
                          <div style={{ color: HOME_TEXT }}>
                            {homeLabel}: {pt.home.toFixed(2)}
                          </div>
                          <div style={{ color: AWAY_TEXT }}>
                            {awayLabel}: {pt.away.toFixed(2)}
                          </div>
                        </div>
                      );
                    }}
                  />

                  {/* Zero axis */}
                  <ReferenceLine y={0} stroke="#64748b" />

                  {/* Halftime mark in the gap between the first and second
                      period groups ("DT" / "HT"). */}
                  {layout.groupGaps
                    .filter((g) => g.afterPeriod === 1)
                    .map((g) => (
                      <ReferenceLine
                        key={`gap-${g.midIdx}`}
                        x={g.midIdx}
                        stroke="#94a3b8"
                        strokeDasharray="4 4"
                        label={{
                          value: t("analytics.momentumHalftimeMark", "HT"),
                          fill: "#cbd5e1",
                          fontSize: 10,
                          fontWeight: 600,
                          position: "insideTop",
                        }}
                      />
                    ))}

                  <Bar
                    dataKey="value"
                    isAnimationActive={false}
                    shape={(props: unknown) => (
                      <MomentumBarShape
                        {...(props as BarShapeProps)}
                        homeColor={HOME_FILL}
                        awayColor={AWAY_FILL}
                      />
                    )}
                  />

                  {/* Goal markers: soccer ball above the top band (home) or
                      below the bottom band (away), at the goal minute. */}
                  {goals.map((g, i) => {
                    const idx = layout.slotByKey.get(
                      `${goalPeriods[i] ?? 1}:${g.minute}`,
                    );
                    if (idx === undefined) return null;
                    const haloColor = g.side === "home" ? HOME_FILL : AWAY_FILL;
                    const teamLabel = g.side === "home" ? homeLabel : awayLabel;
                    return (
                      <ReferenceLine
                        key={`goal-${i}`}
                        x={idx}
                        stroke={haloColor}
                        strokeWidth={1}
                        strokeOpacity={0.35}
                        label={(labelProps) => (
                          <GoalBallLabel
                            viewBox={labelProps.viewBox}
                            haloColor={haloColor}
                            minute={g.minute}
                            side={g.side}
                            teamLabel={teamLabel}
                          />
                        )}
                      />
                    );
                  })}

                  {/* PMA-COCK-192: dismissal markers (straight Red or the
                      merged Yellow (Second)) on the offending team's side. */}
                  {cards.map((c, i) => {
                    const idx = layout.slotByKey.get(
                      `${cardPeriods[i] ?? 1}:${c.minute}`,
                    );
                    if (idx === undefined) return null;
                    const strokeColor =
                      c.side === "home" ? HOME_FILL : AWAY_FILL;
                    const teamLabel = c.side === "home" ? homeLabel : awayLabel;
                    return (
                      <ReferenceLine
                        key={`card-${i}`}
                        x={idx}
                        stroke={strokeColor}
                        strokeWidth={1}
                        strokeOpacity={0.35}
                        label={(labelProps) => (
                          <RedCardLabel
                            viewBox={labelProps.viewBox}
                            minute={c.minute}
                            side={c.side}
                            teamLabel={teamLabel}
                            kind={c.kind}
                            t={t}
                          />
                        )}
                      />
                    );
                  })}
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Bottom directional caption: away below zero axis */}
            <div
              className="flex items-center gap-1 text-[11px] mt-1 select-none"
              style={{ color: AWAY_TEXT }}
            >
              <span aria-hidden="true">▼</span>
              <span>
                {t(
                  "analytics.momentumAwayThreat",
                  "{{team}} more threatening",
                ).replace("{{team}}", awayLabel)}
              </span>
            </div>

            {usedFallback && (
              // Hidden from the PDF/JPG export capture (PMA-COCK-170, client
              // wave 2026-09-07: "cortaría ahí para que no se vea en el pdf
              // lo de cómo se calcula" — the client's request covers this
              // footnote too, not just the explain toggle below).
              <p
                className="text-[10px] text-slate-400 mt-2"
                data-export-hidden="true"
              >
                {t(
                  "analytics.momentumFallbackNote",
                  "Estimated from event types (limited location data)",
                )}
              </p>
            )}
          </>
        )}

        {/* Calculation explanation toggle — always visible on screen, hidden
            from the export capture; see the identical wiring in
            BallRecoveryTimeBanner.tsx. */}
        <div
          className="mt-3 border-t border-slate-700/50 pt-2"
          data-export-hidden="true"
        >
          <button
            type="button"
            className="flex min-h-11 items-center gap-1 py-3 text-xs text-slate-400 hover:text-slate-300 transition-colors"
            aria-expanded={explainOpen}
            data-testid="momentum-explain-toggle"
            onClick={() => setExplainOpen((o) => !o)}
          >
            <span aria-hidden="true">ⓘ</span>
            {t("analytics.momentumExplainToggle", "How is this calculated?")}
            <span aria-hidden="true" className="ml-0.5">
              {explainOpen ? "▲" : "▼"}
            </span>
          </button>
          {explainOpen && (
            <p
              className="mt-2 text-xs text-slate-400 leading-relaxed"
              data-testid="momentum-explain"
            >
              {t(
                "analytics.momentumExplainBody",
                "Shows which team is creating more danger as the match goes on. Every minute we estimate how threatening each team's attacks are — moves into dangerous areas near the goal count for much more than safe passes far from it — and plot the difference. Green above the line means the home team is the bigger threat; blue below means the away team. It captures pressure even when an attack doesn't end in a shot. When precise ball locations aren't available, it's estimated from the types of events logged.",
              )}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
